import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/utils/helpers';
import { MoreVertical, Star, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ChatHistoryProps {
  id: string;
  title: string;
  isFavorite?: boolean;
  isActive?: boolean;
  onClick: () => void;
  onDelete?: (id: string) => void;
  onToggleFavorite?: (id: string) => void;
}

export function ChatHistoryItem({
  id,
  title,
  isActive,
  isFavorite,
  onClick,
  onDelete,
  onToggleFavorite,
}: ChatHistoryProps) {
  const { t } = useTranslation();
  const { t: tPrince } = useTranslation('prince');

  return (
    <div className="relative group">
      <div
        key={id}
        onClick={onClick}
        role="button"
        tabIndex={0}
        aria-label={title}
        className={cn(
          'w-full text-left pl-2 py-2 text-sm transition-all rounded-sm duration-200 cursor-pointer flex items-center text-text-primary',
          isActive ? 'bg-accent font-semibold' : 'hover:bg-accent'
        )}
        data-testid={`chat-history-item-${id}`}
      >
        {isFavorite && (
          <div className="flex-shrink-0 mr-2">
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
          </div>
        )}

        {/* Title with ellipsis and tooltip */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex-1 truncate">{title}</div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs break-words">
              <p className="whitespace-normal">{title}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* More options menu - only show on hover */}
        {(onDelete || onToggleFavorite) && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex-shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  onClick={e => e.stopPropagation()}
                  className="p-1 hover:bg-green-light-4 rounded transition-colors duration-200"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-32">
                {onToggleFavorite && (
                  <DropdownMenuItem
                    onClick={e => {
                      e.stopPropagation();
                      onToggleFavorite(id);
                    }}
                  >
                    <Star className="w-4 h-4 mr-2" />
                    {isFavorite ? tPrince('buttons.unfavorite') : tPrince('buttons.favorite')}
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <DropdownMenuItem
                    onClick={e => {
                      e.stopPropagation();
                      onDelete(id);
                    }}
                    className="text-destructive focus:text-destructive focus:bg-destructive/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t('buttons.delete')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </div>
  );
}
